import { useEffect, type RefObject } from 'react';

// The design's two page-level motion behaviours (W:69-71, W:91, DC:4155-4170), scoped
// to the home page root:
//  - scroll reveal: a section marked data-reveal="out" flips to "in" when it enters
//    the viewport (and every section flips after 3 s, so nothing can stay hidden);
//  - marquee pause: a [data-marquee] track stops (data-mq-off) while off-screen or
//    while the tab is hidden, so idle rails cost nothing.
// Reduced motion is handled in CSS (motion.css): revealed and static regardless.
export function useHomeMotion(root: RefObject<HTMLElement | null>, deps: readonly unknown[]): void {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reveal = () => el.querySelectorAll('[data-reveal="out"]').forEach((n) => n.setAttribute('data-reveal', 'in'));
    if (typeof IntersectionObserver === 'undefined') {
      reveal();
      return;
    }
    const revealIO = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.setAttribute('data-reveal', 'in');
            revealIO.unobserve(en.target);
          }
        }),
      { rootMargin: '0px 0px -8% 0px' },
    );
    el.querySelectorAll('[data-reveal="out"]').forEach((n) => revealIO.observe(n));
    const safety = setTimeout(reveal, 3000);

    const visible = new WeakMap<Element, boolean>();
    const apply = (n: Element) => {
      if (document.hidden || visible.get(n) === false) n.setAttribute('data-mq-off', '');
      else n.removeAttribute('data-mq-off');
    };
    const mqIO = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          visible.set(en.target, en.isIntersecting);
          apply(en.target);
        }),
      { rootMargin: '60px' },
    );
    const tracks = Array.from(el.querySelectorAll('[data-marquee]'));
    tracks.forEach((n) => mqIO.observe(n));
    const onVis = () => tracks.forEach(apply);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearTimeout(safety);
      revealIO.disconnect();
      mqIO.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, deps);
}
